import Vue from 'vue'
import { v4 as uuid } from 'uuid'
import Store from '~store/store'
import CONFIG from '~config/env'
import _, { set, get } from 'lodash'
import moment from 'moment'

const slugify = text => {
  const a = 'àáäâèéëêìíïîòóöôùúüûñçßÿœæŕśńṕẃǵǹḿǘẍźḧ·/_,:;'
  const b = 'aaaaeeeeiiiioooouuuuncsyoarsnpwgnmuxzh______'
  const p = new RegExp(a.split('').join('|'), 'g')

  return text
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/\s+/g, '_') // Replace spaces with -
    .replace(p, c => b.charAt(a.indexOf(c))) // Replace special chars
    .replace(/&/g, '_and_') // Replace & with 'and'
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/__+/g, '_') // Replace multiple - with single -
    .replace(/^_+/, '') // Trim - from start of text
    .replace(/_+$/, '') // Trim - from end of text
}

const concatFileNameAndSize = file => {
  if (!file.name || file.size === undefined) {
    throw new TypeError('file without name or size')
  }
  if (file.name.indexOf('.') === -1) {
    throw new TypeError('filename without extension')
  }
  const name = file.name.substring(0, file.name.lastIndexOf('.'))
  const ext = file.name.substring(file.name.lastIndexOf('.'))
  return name + '--' + file.size + ext
}

const transformConstantObjectInArray = object => {
  const array = []
  for (const key in object) {
    array.push({
      slug: key,
      ...object[key]
    })
  }
  return array
}

const safeEval = (code, context) => {
  // Chaves e valores do contexto, incluindo bibliotecas que queremos expor
  const keys = [...Object.keys(context), 'moment', '_']
  const values = [...Object.values(context), moment, _]

  // Cria uma função com as chaves de contexto e bibliotecas como parâmetros
  // eslint-disable-next-line no-new-func
  const func = new Function(...keys, `${code}`)

  // Executa a função com os valores do contexto e bibliotecas
  return func(...values)
}

const addWorkDays = (date, days, clearTime) => {
  if (!date) return undefined
  if (days === 0) return date
  let foundWorkDay = false
  const momentDate = Vue.moment(date)
  let workdays = 1

  while (!foundWorkDay) {
    momentDate.add(1, 'day')
    if (isWorkTime(momentDate.toDate())) {
      if (days === workdays) {
        foundWorkDay = true
      }
      workdays++
    }
  }
  if (clearTime) {
    return momentDate.set(CONFIG.CLEAR_TIME).toDate()
  }
  return momentDate.toDate()
}

const isWorkTime = date => {
  if (!Store.state.calendarsModule?.selectedCalendar) {
    return true
  }
  const parsedDate = typeof date === 'string' ? new Date(date) : date
  const calendar = Store.state.calendarsModule.selectedCalendar
  let workDay = false

  if (calendar.rules) {
    workDay = calendar.rules[parsedDate.getDay()].working_day
  }

  if (calendar._exceptions) {
    const year = calendar._exceptions[parsedDate.getFullYear()]
    if (year) {
      const month = year[parsedDate.getMonth()]
      if (month) {
        const day = month[parsedDate.getDate()]
        if (day !== undefined) {
          workDay = day
        }
      }
    }
  }

  return workDay
}

const calculateDuration = (startDate, endDate) => {
  let workdays = 1
  let days = 0
  const duration = Vue.moment(endDate).diff(startDate, 'days')
  const refDate = Vue.moment(startDate)

  while (days < duration) {
    days++
    refDate.add(1, 'day')
    if (isWorkTime(refDate.toDate())) {
      workdays++
    }
  }

  return workdays
}

const calculateEndDate = (startDate, duration, dontFormat) => {
  let workdays = 1
  const endDate = Vue.moment(startDate)

  while (workdays < duration || !isWorkTime(endDate.toDate())) {
    endDate.add(1, 'day')
    if (isWorkTime(endDate.toDate())) {
      workdays++
    }
  }
  if (dontFormat) {
    return endDate.toDate()
  }
  return endDate.format('YYYY-MM-DD')
}

const calculateStartDate = (endDate, duration) => {
  let workdays = 1
  const startDate = Vue.moment(endDate)

  while (workdays < duration) {
    startDate.subtract(1, 'day')
    if (isWorkTime(startDate.toDate())) {
      workdays++
    }
  }

  return startDate.format('YYYY-MM-DD')
}

const getContrastTextColor = hex => {
  const threshold = 130 /* about half of 256. Lower threshold equals more dark text on dark background  */

  const hRed = hexToR(hex)
  const hGreen = hexToG(hex)
  const hBlue = hexToB(hex)

  function hexToR(h) {
    return parseInt(cutHex(h).substring(0, 2), 16)
  }
  function hexToG(h) {
    return parseInt(cutHex(h).substring(2, 4), 16)
  }
  function hexToB(h) {
    return parseInt(cutHex(h).substring(4, 6), 16)
  }
  function cutHex(h) {
    return h.charAt(0) === '#' ? h.substring(1, 7) : h
  }

  const cBrightness = (hRed * 299 + hGreen * 587 + hBlue * 114) / 1000
  if (cBrightness > threshold) {
    return '#000000'
  } else {
    return '#ffffff'
  }
}

const CenterPopup = (url, title, w, h) => {
  // Fixes dual-screen position                         Most browsers      Firefox
  const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX
  const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY

  const el = document.documentElement
  const width = window.innerWidth ? window.innerWidth : el.clientWidth ? el.clientWidth : screen.width
  const height = window.innerHeight ? window.innerHeight : el.clientHeight ? el.clientHeight : screen.height

  const systemZoom = width / window.screen.availWidth
  const left = (width - w) / 2 / systemZoom + dualScreenLeft
  const top = (height - h) / 2 / systemZoom + dualScreenTop
  const newWindow = window.open(
    url,
    title,
    'scrollbars=yes, width=' + w / systemZoom + ', height=' + h / systemZoom + ', top=' + top + ', left=' + left
  )

  // Puts focus on the newWindow
  if (window.focus) newWindow.focus()
}

const validCEP = cep => {
  const regex = /^[0-9]{5}-?[0-9]{3}$/
  return regex.test(cep)
}

const validCPF = _strCPF => {
  if (_strCPF === '000.000.000-00') return true
  const strCPF = _strCPF.replace(/\./g, '').replace(/-/g, '')
  let Soma
  let Resto
  Soma = 0
  if (strCPF === '00000000000' || strCPF === '') return false

  for (let i = 1; i <= 9; i++) Soma = Soma + parseInt(strCPF.substring(i - 1, i)) * (11 - i)
  Resto = (Soma * 10) % 11

  if (Resto === 10 || Resto === 11) Resto = 0
  if (Resto !== parseInt(strCPF.substring(9, 10))) return false

  Soma = 0
  for (let i = 1; i <= 10; i++) Soma = Soma + parseInt(strCPF.substring(i - 1, i)) * (12 - i)
  Resto = (Soma * 10) % 11

  if (Resto === 10 || Resto === 11) Resto = 0
  if (Resto !== parseInt(strCPF.substring(10, 11))) return false
  return true
}

const validCNPJ = cnpj => {
  if (cnpj === '00.000.000/0000-00') return true
  cnpj = cnpj.replace(/[^\d]+/g, '')

  if (cnpj.length !== 14) {
    return false
  }

  // Invalid known CNPJs
  if (/^(\d)\1+$/.test(cnpj)) {
    return false
  }

  let length = cnpj.length - 2
  let numbers = cnpj.substring(0, length)
  const digits = cnpj.substring(length)
  let sum = 0
  let pos = length - 7

  for (let i = length; i >= 1; i--) {
    sum += numbers.charAt(length - i) * pos--
    if (pos < 2) {
      pos = 9
    }
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11)
  if (result !== parseInt(digits.charAt(0), 10)) {
    return false
  }

  length += 1
  numbers = cnpj.substring(0, length)
  sum = 0
  pos = length - 7

  for (let i = length; i >= 1; i--) {
    sum += numbers.charAt(length - i) * pos--
    if (pos < 2) {
      pos = 9
    }
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11)
  if (result !== parseInt(digits.charAt(1), 10)) {
    return false
  }

  return true
}

const openLink = link => {
  const linkElement = document.createElement('a')
  linkElement.style.display = 'none'
  linkElement.setAttribute('target', '_blank')
  linkElement.href = link
  document.body.appendChild(linkElement)
  linkElement.click()
  document.body.removeChild(linkElement)
}

const getPropertyCaseInsensitive = (obj, propertyName) => {
  const lowerCasePropertyName = propertyName.toLowerCase()
  for (const key in obj) {
    if (key.toLowerCase() === lowerCasePropertyName) {
      return obj[key]
    }
  }
  return undefined // Property not found
}

const capitalize = nome => {
  return nome.toLowerCase().replace(/(?:^|\s)\S/g, function (a) {
    return a.toUpperCase()
  })
}

export const getObjectBy = (obj, props) => {
  const newObject = {}
  props.forEach(prop => {
    set(newObject, prop, get(obj, prop))
  })
  return newObject
}

const flattenPaths = (obj, path = []) => {
  return Object.keys(obj).reduce((acc, key) => {
    const newPath = path.concat(key)
    if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
      acc.push(...flattenPaths(obj[key], newPath))
    } else {
      acc.push(newPath.join('.'))
    }
    return acc
  }, [])
}

export const downloadFile = (mimeType, filename, text, customName) => {
  const linkElement = document.createElement('a')
  linkElement.style.display = 'none'
  linkElement.setAttribute('target', '_blank')

  if (text && (mimeType === this.MIME_TYPES.TXT || mimeType === this.MIME_TYPES.CSV || mimeType === this.MIME_TYPES.XML)) {
    linkElement.href = 'data:' + mimeType + ';charset=utf-8,' + encodeURIComponent(text)
    linkElement.download = filename
  } else {
    linkElement.href = filename
    const name = filename.substring(filename.lastIndexOf('/') + 1, filename.length)
    linkElement.download = customName || name
  }

  document.body.appendChild(linkElement)
  linkElement.click()
  document.body.removeChild(linkElement)
}

export const maybeCall = (fn, ...args) => {
  if (typeof fn === 'function') {
    return fn(...args)
  }
  return fn
}

export const validDay = v => {
  if (v === null || v === undefined || v === '') return true // Allow empty values
  const day = parseInt(v, 10)
  if (isNaN(day)) return 'O valor deve ser um número.'
  if (day < 1 || day > 31) return 'O dia deve estar entre 1 e 31.'
  return true
}

export default {
  openLink,
  validCPF,
  validCEP,
  validCNPJ,
  concatFileNameAndSize,
  transformConstantObjectInArray,
  uuid,
  slugify,
  safeEval,
  calculateDuration,
  calculateEndDate,
  calculateStartDate,
  getContrastTextColor,
  CenterPopup,
  getPropertyCaseInsensitive,
  capitalize,
  flattenPaths
}

export {
  openLink,
  validCPF,
  validCEP,
  validCNPJ,
  concatFileNameAndSize,
  transformConstantObjectInArray,
  uuid,
  safeEval,
  slugify,
  calculateDuration,
  calculateEndDate,
  calculateStartDate,
  addWorkDays,
  isWorkTime,
  getContrastTextColor,
  CenterPopup,
  capitalize,
  flattenPaths
}
